import { TransactionDiscountAmount } from './TransactionDiscountAmount';
import { TransactionAmount } from './TransactionAmount';
import { TransactionAmountRow } from './TransactionAmountRow';
import { useTranslation } from 'react-i18next';
import { Card, Chip, Separator, Typography } from '@heroui/react';
import type { SalesTransaction } from '@/api/models';
import { getNodeTestIdProp } from '@/utils';
import { TransactionSaleDiscount } from './TransactionSaleDiscount';

type TransactionDetailTotalsProps = { transaction: SalesTransaction };
export const TransactionDetailTotals = ({
    transaction,
}: TransactionDetailTotalsProps) => {
    const { t } = useTranslation('pages');
    return (
        <Card
            variant="secondary"
            className="gap-0 overflow-hidden p-0"
            {...getNodeTestIdProp('transaction-detail-totals')}
        >
            <div {...getNodeTestIdProp('transaction-detail-subtotal-row')}>
                <TransactionAmountRow
                    label={
                        <Typography
                            type="body-sm"
                            color="muted"
                            {...getNodeTestIdProp(
                                'transaction-detail-subtotal-label',
                            )}
                        >
                            {t('sales.widgets.transactions.modal.subtotal')}
                        </Typography>
                    }
                >
                    <Typography type="body-sm" weight="medium">
                        <span
                            {...getNodeTestIdProp(
                                'transaction-detail-subtotal',
                            )}
                        >
                            <TransactionAmount
                                amountTetri={transaction.subtotalTetri}
                            />
                        </span>
                    </Typography>
                </TransactionAmountRow>
            </div>
            {transaction.lineDiscountTotalTetri > 0 && (
                <>
                    <Separator />
                    <div
                        {...getNodeTestIdProp(
                            'transaction-detail-line-discounts-row',
                        )}
                    >
                        <TransactionAmountRow
                            label={
                                <Typography type="body-sm" color="muted">
                                    {t(
                                        'sales.widgets.transactions.modal.lineDiscounts',
                                    )}
                                </Typography>
                            }
                        >
                            <span
                                {...getNodeTestIdProp(
                                    'transaction-detail-line-discounts',
                                )}
                            >
                                <TransactionDiscountAmount
                                    amountTetri={
                                        transaction.lineDiscountTotalTetri
                                    }
                                />
                            </span>
                        </TransactionAmountRow>
                    </div>
                </>
            )}
            <TransactionSaleDiscount discount={transaction.cartDiscount} />
            {transaction.feesTetri > 0 && (
                <>
                    <Separator />
                    <div {...getNodeTestIdProp('transaction-detail-fees-row')}>
                        <TransactionAmountRow
                            label={
                                <Typography type="body-sm" color="muted">
                                    {t('sales.widgets.transactions.modal.fees')}
                                </Typography>
                            }
                        >
                            <Typography type="body-sm" weight="medium">
                                <span
                                    {...getNodeTestIdProp(
                                        'transaction-detail-fees',
                                    )}
                                >
                                    <TransactionAmount
                                        amountTetri={transaction.feesTetri}
                                    />
                                </span>
                            </Typography>
                        </TransactionAmountRow>
                    </div>
                </>
            )}
            <Separator />
            <div
                className="flex items-center justify-between gap-3 px-4 py-3"
                {...getNodeTestIdProp('transaction-detail-grand-total-row')}
            >
                <Typography type="body-sm" weight="bold">
                    {t('sales.widgets.transactions.modal.grandTotal')}
                </Typography>
                <Chip color="success" variant="tertiary">
                    <Chip.Label>
                        <span
                            {...getNodeTestIdProp(
                                'transaction-detail-grand-total',
                            )}
                        >
                            <TransactionAmount
                                amountTetri={transaction.totalTetri}
                            />
                        </span>
                    </Chip.Label>
                </Chip>
            </div>
        </Card>
    );
};
